// 🎯 Hook personalizado para la recepción de devoluciones - Custom hook for returned items reception
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@shared/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { ReceiveApiAdapter } from '../adapters/ReceiveApiAdapter';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
export const useReceiveReturned = (returnedId: number) => {
  const { t } = useTranslation('receiveModule');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const receiveAdapter = new ReceiveApiAdapter(createHttpClient(apiClientWH));

  // 🎯 Query para obtener datos específicos del rehusado por búsqueda
  const {
    data: returnedListData,
    isLoading: isLoadingList,
    error: listError,
  } = useQuery({
    queryKey: ['returnedListForReceive', returnedId],
    queryFn: async () => {
      // Llamada directa con solo el parámetro search
      const http = createHttpClient(apiClientWH);
      const response = await http.get<any>('/returned-receptions', {
        search: returnedId.toString(),
      });

      // Verificar que se encontró el item
      const foundItem = response.data.find((item: any) => item.id === returnedId);

      if (!foundItem) {
        throw new Error(`No se encontró el rehusado con ID ${returnedId}`);
      }

      // Enriquecer con info de productos (como en Packing)
      const productIds = (foundItem.lines || []).map((l: any) => l.product_id);
      const uniqueIds = Array.from(new Set(productIds));
      let productsMap = new Map<number, any>();
      if (uniqueIds.length > 0) {
        const http = createHttpClient(apiClientWH);
        // 🎯 Agregar per_page: 100 para evitar límite de paginación de 25
        const productsResp = await http.get<any>('/products', {
          fields: 'basic',
          product_ids: uniqueIds.join(','),
          per_page: 1000,
        });
        const products = productsResp.data || [];
        productsMap = new Map<number, any>(products.map((p: any) => [p.id, p]));
      }

      const enriched = {
        ...foundItem,
        lines: (foundItem.lines || []).map((line: any) => ({
          ...line,
          product: productsMap.get(line.product_id) || null,
        })),
      } as any;

      return enriched;
    },
    enabled: !!returnedId && !isNaN(returnedId), // Solo ejecutar si returnedId es un número válido
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  // 🎯 Query para obtener datos específicos de la recepción (COMENTADO TEMPORALMENTE)
  // const {
  //   data: receiveData,
  //   isLoading: isLoadingReceive,
  //   error: receiveError,
  //   refetch: refetchReceive,
  // } = useQuery({
  //   queryKey: ['receiveReturned', returnedId],
  //   queryFn: () => receiveAdapter.getReceiveReturnedById(returnedId),
  //   staleTime: 1 * 60 * 1000, // 1 minuto
  //   gcTime: 5 * 60 * 1000, // 5 minutos
  // });

  // 🎯 Combinar datos de la lista con datos de recepción
  const returnedData = returnedListData
    ? {
        id: returnedListData.id,
        code: returnedListData.code,
        status: returnedListData.status,
        date: returnedListData.date,
        // Incluir location_id para detectar carrito asignado previamente
        location_id: (returnedListData as any).location_id,
        orders_count: returnedListData.orders_count,
        // Usar líneas provenientes del endpoint /returned-receptions (enriquecidas con producto)
        lines: returnedListData.lines ?? [],
      }
    : null;

  const isLoading = isLoadingList;
  const error = listError;
  const refetch = () => {
    queryClient.invalidateQueries({
      queryKey: ['returnedListForReceive', returnedId],
    });
  };

  // 🎯 Query secundaria: obtener órdenes por ids de las lines
  const ordersQuery = useQuery({
    queryKey: ['receiveOrders', returnedId, returnedData?.lines?.length || 0],
    queryFn: async () => {
      if (!returnedData?.lines || returnedData.lines.length === 0) return [];
      const ids = Array.from(new Set((returnedData.lines as any[]).map((l: any) => l.order_id))).filter(Boolean);
      if (ids.length === 0) return [];
      const http = createHttpClient(apiClientWH);
      // 🎯 Agregar per_page: 1000 para evitar límite de paginación de 25
      const resp = await http.get<any>('/orders', {
        ids: ids.join(','),
        per_page: 1000,
      });
      return resp.data || [];
    },
    enabled: !!returnedListData && !!returnedId && !isNaN(returnedId), // También aquí
    staleTime: 5 * 60 * 1000,
  });

  // 🎯 Mutation para recepcionar líneas
  const receiveMutation = useMutation({
    mutationFn: (data: { order_id: number; lines: Array<{ product_id: number; quantity: number }> }) =>
      receiveAdapter.receiveReturnedLines(returnedId, data),
    onSuccess: (response) => {
      // Cualquier respuesta 200 OK sin cuerpo se considera exitosa
      toast({
        title: t('toasts.success.title'),
        description: (response as any)?.message || t('toasts.receive.success'),
        variant: 'default',
      });

      // 🎯 Invalidar queries relacionadas para forzar refetch
      queryClient.invalidateQueries({ queryKey: ['receiveReturned', returnedId] });
      queryClient.invalidateQueries({ queryKey: ['returnedListForReceive', returnedId] });
      queryClient.invalidateQueries({ queryKey: ['receiveOrders', returnedId] });
      queryClient.invalidateQueries({ queryKey: ['logisticStatusCounts'] });
      queryClient.invalidateQueries({ queryKey: ['returned'] });
    },
    onError: (error: any) => {
      console.log('🔍 useReceiveReturned - API Error:', error);
      console.log('🔍 error.response:', error.response);
      console.log('🔍 error.response.data:', error.response?.data);

      // Extraer mensaje de error de la API
      let errorMessage = t('toasts.receive.unexpected');

      if (error?.response?.data?.error?.message) {
        // Error con estructura: { error: { message: "..." } }
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        // Error con estructura: { message: "..." }
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        // Error genérico
        errorMessage = error.message;
      }

      toast({
        title: t('toasts.error.title'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  // 🎯 Mutation para finalizar rehusado
  const finalizeMutation = useMutation({
    mutationFn: () => receiveAdapter.finalizeReception(returnedId),
    onSuccess: (response) => {
      // Cualquier respuesta 200 OK sin cuerpo se considera exitosa
      toast({
        title: t('toasts.finish.success'),
        description: (response as any)?.message || t('toasts.finish.success'),
        variant: 'default',
      });

      // 🎯 Invalidar queries relacionadas para forzar refetch
      queryClient.invalidateQueries({
        queryKey: ['returnedListForReceive', returnedId],
      });
      queryClient.invalidateQueries({ queryKey: ['logisticStatusCounts'] });
      queryClient.invalidateQueries({ queryKey: ['returned'] });
    },
    onError: (error: any) => {
      console.log('🔍 useReceiveReturned - Finalize Error:', error);

      // Extraer mensaje de error de la API
      let errorMessage = t('toasts.finish.unexpected');

      if (error?.response?.data?.error?.message) {
        errorMessage = error.response.data.error.message;
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error?.message) {
        errorMessage = error.message;
      }

      toast({
        title: t('toasts.finish.error'),
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  return {
    returnedData,
    isLoading,
    error: error?.message || null,
    refetch,
    receiveLines: receiveMutation.mutate,
    isReceiving: receiveMutation.isPending,
    finalizeReturned: finalizeMutation.mutate,
    isFinalizing: finalizeMutation.isPending,
    orders: (ordersQuery.data as any[]) || [],
    ordersLoading: ordersQuery.isLoading,
    ordersError: (ordersQuery.error as any)?.message || null,
  };
};
